import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import {
  Component,
  HostBinding,
  Input,
  OnInit,
  ChangeDetectorRef,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CommonStylesForRender } from 'render/render.models';
import { RenderConfigurationService } from 'render/services/render-configuration.service';
import { UnsubscribeComponent } from 'shared/directives/unsubscribe.component';
import { ResponsiveSetting } from 'shared/responsive-settings/responsive-settings.interface';
import { filterDict } from 'shared/utils/functools-map';


interface HostStyles {
  backgroundColor: string;
  marginTop: string;
  marginRight: string;
  marginLeft: string;
  marginBottom: string;
}

@Component({
  selector: 'idt-common-styles-render',
  templateUrl: './common-styles-render.component.html',
  styleUrls: ['./common-styles-render.component.scss']
})
export class CommonStylesRenderComponent extends UnsubscribeComponent implements OnInit, OnChanges {

  @Input() public styles?: CommonStylesForRender;
  @Input() public hover?: boolean;
  @Input() public pressed?: boolean;

  @HostBinding('style.background-color')
  public backgroundColor!: string;

  @HostBinding('style.margin-block-start')
  public marginTop!: string;

  @HostBinding('style.margin-inline-end')
  public marginRight!: string;

  @HostBinding('style.margin-inline-start')
  public marginLeft!: string;

  @HostBinding('style.margin-block-end')
  public marginBottom!: string;

  private readonly styles$ = new BehaviorSubject<CommonStylesForRender | undefined>(undefined);

  constructor(
    private readonly breakpointObserver: BreakpointObserver,
    private readonly renderConfigService: RenderConfigurationService,
    private readonly cd: ChangeDetectorRef
  ) {
    super();
  }

  @HostBinding('style.border')
  public get componentBorder(): CommonStylesForRender['borderColor'] {
    let result = 'none';
    if (this.styles !== undefined) {
      const { borderStyle, borderWidth } = this.styles;
      let borderColor: string | undefined;
      if (this.pressed && this.styles.pressedBorderColor) {
        borderColor = this.styles.pressedBorderColor;
      } else if (this.hover && this.styles.hoverBorderColor) {
        borderColor = this.styles.hoverBorderColor;
      } else {
        borderColor = this.styles.borderColor;
      }

      result = `${borderWidth!} ${borderStyle!} ${borderColor!}`;
    }
    return result;
  }

  @HostBinding('style.border-radius')
  @HostBinding('class.overflow-hidden')
  public get componentBorderRadius(): CommonStylesForRender['borderRadius'] {
    if (this.styles?.borderRadiusType !== 'custom') {
      return undefined;
    }

    let result = 'unset';
    if (this.styles?.borderRadius && this.styles.borderRadius !== undefined) {
      result = this.styles.borderRadius;
    }
    return (result !== '0px') ? result : undefined;
  }

  @HostBinding('class')
  public get classes(): string | undefined {
    let classes = (this.styles?.shadow && this.styles.shadow !== 'none') ? `${this.styles?.shadow} ` : '';

    if (this.styles?.borderRadiusType) {
      if (this.styles.borderRadiusType === 'straight') {
        classes += 'border-radius-straight overflow-hidden';
      } else if (this.styles.borderRadiusType === 'semi-round') {
        classes += 'border-radius-semi-round overflow-hidden';
      } else if (this.styles.borderRadiusType === 'round') {
        classes += 'border-radius-round overflow-hidden';
      }
    }

    return classes;
  }

  public ngOnInit(): void {
    this.setDefaultHostStyles();
    combineLatest([
      this.styles$,
      this.breakpointObserver.observe(this.renderConfigService.config.breakpoints.map((breakpoint) => breakpoint.bp))
    ])
      .pipe(
        takeUntil(this.clearSubscriptions$)
      )
      .subscribe(([, breakpointState]) => {
        this.onMediaChange(breakpointState);
        this.cd.markForCheck();
      });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.styles) {
      this.styles$.next(this.styles);
    }
  }

  private onMediaChange(breakpointState: BreakpointState): void {
    const matches = filterDict(breakpointState.breakpoints, (key: string, value: boolean) => value);

    const matchKeys = Object.keys(matches);

    if (matchKeys.length) {
      const mainMatch = matchKeys[0];
      this.setBreakpointStylesMatch(mainMatch);
    } else {
      this.setDefaultHostStyles();
    }
  }

  private setBreakpointStylesMatch(breakpoint: string): void {
    if (this.styles === undefined) {
      return;
    }

    const defaultBpSettings = {
      marginTop: this.toUnit(this.styles.marginTop ?? ''),
      marginBottom: this.toUnit(this.styles.marginBottom ?? ''),
      marginLeft: this.toUnit(this.styles.marginLeft ?? ''),
      marginRight: this.toUnit(this.styles.marginRight ?? '')
    };

    const responsiveSettings: ResponsiveSetting[] = this.styles.responsiveConfig || [];

    const breakpointStyles = responsiveSettings.find(
      (responsiveSetting) => responsiveSetting.bp === breakpoint || responsiveSetting.id === breakpoint
    ) || {} as ResponsiveSetting;

    this.setHostStyles({
      backgroundColor: this.styles.bgColor ?? '',
      marginTop: breakpointStyles.marginTop || defaultBpSettings.marginTop,
      marginRight: breakpointStyles.marginRight || defaultBpSettings.marginRight,
      marginLeft: breakpointStyles.marginLeft || defaultBpSettings.marginLeft,
      marginBottom: breakpointStyles.marginBottom || defaultBpSettings.marginBottom
    });
  }

  private setDefaultHostStyles(): void {
    if (this.styles === undefined) {
      return;
    }

    this.setHostStyles({
      backgroundColor: this.styles.bgColor ?? '',
      marginTop: this.toUnit(this.styles.marginTop ?? ''),
      marginRight: this.toUnit(this.styles.marginRight ?? ''),
      marginLeft: this.toUnit(this.styles.marginLeft ?? ''),
      marginBottom: this.toUnit(this.styles.marginBottom ?? '')
    });
  }

  private setHostStyles(styles: HostStyles): void {
    this.backgroundColor = styles.backgroundColor;
    this.marginTop = styles.marginTop;
    this.marginRight = styles.marginRight;
    this.marginLeft = styles.marginLeft;
    this.marginBottom = styles.marginBottom;
  }

  private toUnit(value: number | string): string {
    return (typeof value === 'number') ? `${value}%` : value;
  }

}
